import { IconPlaneTakeOff16 } from '@warp-ds/icons/react/plane-take-off-16';
import { Button } from '@warp-ds/react/components/button';
import cn from 'classnames';

import { useConfig } from 'shared/config/ConfigContext';
import { formatReadableDateFullNoYear, parseISO } from 'shared/util/datetime-fns';
import { thousandSeparated } from 'shared/util/formatting';
import { StarRating } from '@/components/resultList/components/StarRating';
import { SupplierLogo } from '@/components/resultList/images/SupplierLogo';
import type { LmsOffer } from '@/interfaces/lms';
import { trackLmsClickOffer } from '@/lib/utils/tracking';
import displayValues from '@/resources/displayValues.json';

interface LmsResultListItemProps {
    offer: LmsOffer;
    isLoading: boolean;
}

export const LmsResultListItem = ({ offer, isLoading }: LmsResultListItemProps) => {
    const { contextPathLMS } = useConfig();

    const joinLocation = (...parts: Array<string | null | undefined>) =>
        parts
            .map((s) => s?.trim())
            .filter((s): s is string => s && s.length > 0)
            .join(', ');

    const renderTripType = (tripType: string, hotelName: string): string => {
        switch (tripType) {
            case 'SPECIFIED':
                return hotelName;
            case 'FLIGHT_ONLY':
                return displayValues.travelType.fly;
            case 'ONE_WAY_FLIGHT':
                return displayValues.travelType.enveisfly;
            case 'UNSPECIFIED':
                return displayValues.travelType.uspesifisert;
            default:
                return 'Ukjent hotell/fly'; // should not be possible
        }
    };

    return (
        <article
            className={cn('flex flex-col gap-16 s-bg rounded-8 border md:flex-row p-16', {
                'animate-inprogress inset-0': isLoading,
            })}
            data-testid={`resultListItem-${offer.offerId}`}
            data-result-item
            tabIndex={-1}>
            <div className="flex-1 flex flex-col gap-8">
                <header className="flex flex-col gap-8">
                    <span className="text-caption" data-testid="offerOrigin">
                        {displayValues.originAirports[offer.originAirportCode]}
                    </span>
                    <h2 className="t3 mb-0" data-testid="offerDestination">
                        {joinLocation(offer.destination, offer.region, offer.country)}
                    </h2>
                </header>
                <p className="flex flex-row items-center gap-12 text-caption my-8">
                    <IconPlaneTakeOff16 />
                    <span data-testid="offerDepartureDate">{formatReadableDateFullNoYear(parseISO(offer.outboundDepartureTime))}</span>
                    {offer.duration > 1 && (
                        <span className="border-l pl-12" data-testid="offerDuration">
                            {offer.duration} dager
                        </span>
                    )}
                </p>
                <div className="flex flex-row gap-8 items-center text-body" data-testid="offerHotelName">
                    <span className="truncate">{renderTripType(offer.tripType, offer.hotelName)}</span>
                    {offer.tripType === 'SPECIFIED' && <StarRating id={`rating-${offer.offerId}`} rating={offer.rating} emptyStars />}
                </div>
            </div>
            <footer className="flex flex-row md:flex-col justify-between items-center md:items-end">
                <SupplierLogo supplier={offer.supplier} />
                <Button
                    primary
                    href={`${contextPathLMS}/ut?offerId=${offer.offerId}&fromPage=result`}
                    onClick={() => trackLmsClickOffer(offer)}
                    data-testid="offerLink"
                    rel="nofollow noopener"
                    target="_blank">
                    {thousandSeparated(offer.price)} kr
                </Button>
            </footer>
        </article>
    );
};
